<template>
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ t('calendar.title') }}</h4>

        <!-- Filter Button -->
        <Button @click="showFilterDialog = !showFilterDialog" icon="fa-solid fa-filter" :label="t('filters.title')"
            :badge="activeFiltersCount > 0 ? String(activeFiltersCount) : undefined" badgeClass="p-badge-success"
            severity="primary" />
    </div>

    <!-- Calendar -->
    <CalendarView ref="calendarViewRef" :event-source="eventSourceFunc" @event-click="handleEventClick"
        @event-drop="handleEventDrop" @event-resize="handleEventResize" @select-slot="handleSelectSlot" />

    <!-- Filters Drawer -->
    <CalendarFilters v-model:visible="showFilterDialog" :filters="filters" :resource-types="resourcesStore.types"
        :filtered-resources="filteredResources" :has-active-filters="hasActiveFilters" @update:filters="onFiltersUpdate"
        @clear-filters="clearFilters" />

    <!-- Modals -->
    <ReservationDetail :open="isModalOpen" :reservation="selectedReservation"
        :highlight-resource-id="selectedResourceId" @close="closeModal" @canceled="handleReservationCanceled"
        @confirmed="handleCompleteReservation" />

    <ReservationChange :open="isChangeModalOpen" :reservation="pendingReservation" :change="pendingChange"
        :resource-name="pendingResourceName" @confirm="confirmChange" @cancel="cancelChange" />

    <CreateReservation :open="createModalOpen" :start-ms="selectedStartMs ?? Date.now()"
        :end-ms="selectedEndMs ?? Date.now()" @confirm="confirmOpenCreatePage" @cancel="closeCreateModal" />
</template>

<script setup lang="ts">
import CalendarFilters from '@/components/calendar/CalendarFilters.vue';
import CalendarView from '@/components/calendar/CalendarView.vue';
import CreateReservation from '@/components/modal/CreateReservation.vue';
import ReservationChange from '@/components/modal/ReservationChange.vue';
import ReservationDetail from '@/components/modal/ReservationDetail.vue';
import { useCalendarPage } from '@/composables/useCalendarPage';
import { resourceLocalWallClockDateToMs } from '@/utils/date';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();

const calendarViewRef = ref<InstanceType<typeof CalendarView> | null>(null);

// Calendar logic
const {
    // Stores
    resourcesStore,

    // Filter state
    showFilterDialog,
    filters,
    filteredResources,
    hasActiveFilters,
    activeFiltersCount,
    clearFilters,

    // Reservation detail modal
    selectedReservation,
    selectedResourceId,
    isModalOpen,
    closeModal,

    // Create reservation modal
    createModalOpen,
    selectedStartMs,
    selectedEndMs,
    openCreateModal,
    closeCreateModal,

    // Change reservation modal
    pendingChange,
    pendingReservation,
    pendingResourceName,
    isChangeModalOpen,
    confirmChange,
    cancelChange,

    // Event source and handlers
    eventSourceFunc,
    handleEventClick,
    handleEventDrop,
    handleEventResize,

    // Lifecycle
    initialize
} = useCalendarPage();

// Handle filter updates
function onFiltersUpdate(newFilters: typeof filters.value) {
    // Clear resources that don't match the selected types
    if (newFilters.resourceTypeIds.length > 0 && newFilters.resourceTypeIds !== filters.value.resourceTypeIds) {
        const validResourceIds = filteredResources.value.map(r => r.id);
        newFilters.resourceIds = newFilters.resourceIds.filter(id => validResourceIds.includes(id));
    }

    // Clear visitor types when switching to internal
    if (newFilters.reservationType === 'INTERNAL') {
        newFilters.visitorTypes = [];
    }

    filters.value = newFilters;
}

// Handle select slot
function handleSelectSlot(startMs: number, endMs: number) {
    openCreateModal(startMs, endMs);
}

// Handle create reservation confirmation
function confirmOpenCreatePage(resourceIds: string[]) {
    if (!selectedStartMs.value || !selectedEndMs.value || resourceIds.length === 0) return;

    // THE UN-SHIFT for the drag-select. The grid now draws each space on its own clock, so what the operator dragged
    // is a wall clock in the SPACE's zone, not an instant. It cannot be un-shifted at the emit (CalendarView's
    // selection carries no space — the operator picks one here, afterwards), so this is the first point in the chain
    // that knows one.
    //
    // The zone comes from the FIRST selected space, the same single-answer rule D-11 already applies to the wizard's
    // shared date — see the comment at NewReservation.vue's query-param read for why.
    //
    // The query param's contract is UNCHANGED: it still carries a true instant. This half and the wizard's existing
    // D-13/SC4 conversion are a matched pair — the wizard turns this true instant back into the space's wall clock,
    // which is the wall clock that was dragged. Neither half can be removed alone.
    //
    // selectedStartMs / selectedEndMs themselves stay display instants on purpose: CreateReservation renders them
    // browser-locally (so the operator sees the clock they dragged) and filters availableResources by minutes-of-day
    // against each space's own availability windows. Both are correct on the display instant.
    const zoneId = resourcesStore.getResourceById(resourceIds[0])?.timezone?.id ?? null;
    const startMs = resourceLocalWallClockDateToMs(zoneId, new Date(selectedStartMs.value)) ?? selectedStartMs.value;
    const endMs = resourceLocalWallClockDateToMs(zoneId, new Date(selectedEndMs.value)) ?? selectedEndMs.value;

    router.push({
        name: 'new',
        query: {
            start: String(startMs),
            end: String(endMs),
            resourceIds: resourceIds.join(',')
        }
    });

    closeCreateModal();
}

// Handle reservation cancellation
function handleReservationCanceled() {
    refetchEvents();
}

function handleCompleteReservation() {
    refetchEvents();
}

// Refetch calendar events
function refetchEvents() {
    calendarViewRef.value?.refetchEvents();
}

// Watch for filter changes and refetch events
watch(
    filters,
    () => {
        refetchEvents();
    },
    { deep: true }
);

// Lifecycle
onMounted(async () => {
    await initialize();
});
</script>
